import type { DodgeDetectionConfig, DodgeDirection, MotionSample } from '../types';

export type DodgeDetectionEvent = {
  direction: DodgeDirection | null;
  source: 'accel' | 'gyro' | 'none';
  strength: number;
};

export class DodgeDetector {
  constructor(private readonly config: DodgeDetectionConfig) {}

  process(sample: MotionSample): DodgeDetectionEvent {
    const accel = this.directionFromAcceleration(sample);
    if (accel.direction) return accel;
    return this.directionFromGyro(sample);
  }

  private directionFromAcceleration(sample: MotionSample): DodgeDetectionEvent {
    const x = finite(sample.x);
    const y = finite(sample.y);
    if (x === null || y === null) return { direction: null, source: 'none', strength: 0 };

    const absX = Math.abs(x);
    const absY = Math.abs(y);
    const ratio = this.config.dominantAxisRatio;
    if (absY >= this.config.accelThreshold && absY >= absX * ratio) {
      return { direction: y > 0 ? 'UP' : 'DOWN', source: 'accel', strength: absY };
    }
    if (absX >= this.config.accelThreshold && absX >= absY * ratio) {
      return { direction: x > 0 ? 'RIGHT' : 'LEFT', source: 'accel', strength: absX };
    }
    return { direction: null, source: 'none', strength: Math.max(absX, absY) };
  }

  private directionFromGyro(sample: MotionSample): DodgeDetectionEvent {
    const x = finite(sample.x);
    const y = finite(sample.y);
    const beta = finite(sample.rotationRate?.beta);
    const gamma = finite(sample.rotationRate?.gamma);
    if (beta === null || gamma === null) return { direction: null, source: 'none', strength: 0 };

    const absBeta = Math.abs(beta);
    const absGamma = Math.abs(gamma);
    const ratio = this.config.dominantAxisRatio;
    if (absBeta >= this.config.gyroThreshold && absBeta >= absGamma * ratio) {
      if (this.hasMatchingAccelAssist(y, beta)) {
        return { direction: beta > 0 ? 'UP' : 'DOWN', source: 'gyro', strength: absBeta };
      }
    }
    if (absGamma >= this.config.gyroThreshold && absGamma >= absBeta * ratio) {
      if (this.hasMatchingAccelAssist(x, gamma)) {
        return { direction: gamma > 0 ? 'RIGHT' : 'LEFT', source: 'gyro', strength: absGamma };
      }
    }
    return { direction: null, source: 'none', strength: Math.max(absBeta, absGamma) };
  }

  private hasMatchingAccelAssist(accel: number | null, gyro: number): boolean {
    if (accel === null) return false;
    const assistThreshold = this.config.accelThreshold * 0.45;
    return Math.abs(accel) >= assistThreshold && Math.sign(accel) === Math.sign(gyro);
  }
}

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
